/**
 * Payment methods — the collection registry.
 *
 * These are the accounts money is collected into. The deposit router picks
 * between them by health, then remaining headroom, then weighted round-robin, so
 * every column here is an input to that decision:
 *
 *   health     a degraded or down method is never selected
 *   headroom   daily limit less used today; a method at its cap is never selected
 *   weight     relative share among methods with equal headroom
 *
 * Headroom is the operational number. A method running out mid-evening pushes
 * every deposit onto the remaining accounts, and if they exhaust too the player
 * gets `no_method` and simply cannot pay. The usage bar is here so that is seen
 * an hour before it happens rather than in the support queue afterwards.
 *
 * Full account addresses are deliberately not shown. A collection address is
 * revealed once, to one player, attached to one deposit — that is the same
 * reason `/deposits/methods` aggregates per kind rather than listing accounts.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createPaymentMethod,
  fetchPaymentLimits,
  fetchPaymentMethods,
  updatePaymentLimits,
  updatePaymentMethod,
} from '../api/client.ts';
import type {
  AdminPaymentMethod,
  ChainId,
  Currency,
  CryptoAsset,
  MethodHealth,
  MethodKind,
  PaymentLimits,
  PaymentMethodOptions,
  Region,
} from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { add, cmp, isAmount, percent, sub } from '../lib/money.ts';
import {
  Card,
  EmptyState,
  ErrorState,
  Loading,
  Money,
  Stat,
  StatusBadge,
  TableSkeleton,
  UsageMeter,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconCheck, IconRefresh, IconSliders, IconVault } from '../ui/icons.tsx';

/** The table's shape, reused by the loading skeleton so nothing jumps. */
const COLUMNS = [
  'text',
  'text',
  'text',
  'text',
  'text',
  'num',
  'num',
  'num',
  'num',
  'num',
  'num',
  'num',
  'text',
] as const;

export function PaymentMethods(): ReactNode {
  const methods = useAsync(() => fetchPaymentMethods(), []);
  const [creating, setCreating] = useState(false);
  const rows = methods.data?.methods ?? [];
  const options = methods.data?.options ?? null;

  const totalLimit = rows.filter((m) => m.enabled).map((m) => m.dailyLimit);
  const totalUsed = rows.filter((m) => m.enabled).map((m) => m.usedToday);

  return (
    <div className="stack">
      <div className="toolbar">
        <span className="toolbar__note">
          The router selects only methods that are enabled, healthy and still have headroom today.
          When the last one runs out, players get <code>no_method</code> and simply cannot pay.
        </span>
        <button
          type="button"
          className={`btn btn--sm ${creating ? 'btn--ghost' : 'btn--primary'}`}
          onClick={() => setCreating((current) => !current)}
        >
          <IconVault size="sm" />
          {creating ? 'Close form' : 'Register method'}
        </button>
        <button type="button" className="btn btn--sm btn--ghost" onClick={methods.reload}>
          <IconRefresh size="sm" />
          Refresh
        </button>
      </div>

      {creating && options ? (
        <CreateMethodForm
          options={options}
          onDone={() => {
            setCreating(false);
            methods.reload();
          }}
        />
      ) : null}

      {rows.length > 0 ? (
        <CapacitySummary limits={totalLimit} used={totalUsed} rows={rows} />
      ) : null}

      <LimitsCard />

      <Card title="Collection methods" icon={<IconVault />} count={rows.length} flush>
        {methods.error && rows.length === 0 ? (
          <ErrorState
            title="The collection registry did not load"
            detail={methods.error}
            onRetry={methods.reload}
          />
        ) : methods.loading && rows.length === 0 ? (
          <TableSkeleton columns={COLUMNS} rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconVault size="lg" />}
            title="No collection methods are registered"
            hint="With nothing registered the deposit router has nowhere to send a player, and every deposit fails."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Kind</th>
                  <th>Rail</th>
                  <th>Entity</th>
                  <th>Health</th>
                  <th className="right">Weight</th>
                  <th className="right">Daily cap</th>
                  <th className="right">Used today</th>
                  <th className="right">Headroom</th>
                  <th className="right">Usage</th>
                  <th className="right" title="Modelled cost of this rail — never charged to a player; feeds the NGR report">
                    Fee
                  </th>
                  <th className="right">Deposits</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <MethodRow key={row.id} row={row} onChanged={methods.reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function CapacitySummary({
  limits,
  used,
  rows,
}: {
  limits: string[];
  used: string[];
  rows: AdminPaymentMethod[];
}): ReactNode {
  const capacity = limits.reduce((total, value) => add(total, value), '0.0000');
  const consumed = used.reduce((total, value) => add(total, value), '0.0000');
  const headroom = sub(capacity, consumed);
  const share = percent(consumed, capacity);
  const selectable = rows.filter(
    (m) => m.enabled && m.health === 'healthy' && cmp(m.usedToday, m.dailyLimit) < 0,
  ).length;

  return (
    <>
      <div className="stats stats--lead">
        <Stat
          lead
          icon={<IconVault size="sm" />}
          label="Headroom left today"
          value={<Money value={headroom} currency />}
          tone={cmp(headroom, '0') <= 0 ? 'loss' : undefined}
          note="what can still be collected before deposits start failing"
        />
        <Stat
          lead
          label="Selectable methods"
          value={selectable.toLocaleString()}
          tone={selectable === 0 ? 'loss' : selectable === 1 ? 'pending' : undefined}
          note="healthy, enabled and under cap — the router will use these and no others"
        />
      </div>

      <div className="stats">
        <Stat
          label="Collection capacity today"
          value={<Money value={capacity} currency />}
          note="across enabled methods"
        />
        <Stat
          label="Used today"
          value={<Money value={consumed} currency />}
          note={share === null ? '—' : `${share}% of capacity`}
        />
      </div>
    </>
  );
}

/**
 * Registering a collection endpoint. The address is written exactly once, here
 * — there is deliberately no edit path for it afterwards. A retired or
 * compromised account is disabled and a new method registered in its place
 * (retire-and-replace), so every rupee stays attributable to the address it
 * was actually paid to.
 *
 * **Every choice on this form comes from the server** (`options`, derived on
 * each request from the ADR-021 chain table and the T-122 region table). Until
 * T-192 this form offered three kinds and posted `kind, label, ownerEntity,
 * address, displayName, dailyLimit, weight` and nothing else — no currency, no
 * region, no chain, no asset. The create route refuses a crypto or wallet
 * method that does not name a chain and an asset, so choosing "Crypto" here
 * ALWAYS 400'd, and `currency` defaulted to INR and `region` to IN, so no USDT,
 * USD, EUR or BRL rail could be registered from the panel either. The whole
 * multi-currency, multi-chain design was reachable only by curl.
 */
function CreateMethodForm({
  options,
  onDone,
}: {
  options: PaymentMethodOptions;
  onDone: () => void;
}): ReactNode {
  const [kind, setKind] = useState<MethodKind>('upi');
  const [label, setLabel] = useState('');
  const [ownerEntity, setOwnerEntity] = useState('');
  const [address, setAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [dailyLimit, setDailyLimit] = useState('');
  const [weight, setWeight] = useState('100');
  const [region, setRegion] = useState<Region>('IN');
  const [currency, setCurrency] = useState<Currency>('INR');
  const [chain, setChain] = useState<ChainId>(options.chains[0]?.id ?? 'EVM');
  const [asset, setAsset] = useState<CryptoAsset | ''>('');
  const [feePct, setFeePct] = useState('0');
  const [feeFixed, setFeeFixed] = useState('0');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const kindSpec = options.kinds.find((k) => k.id === kind) ?? null;
  const needsChain = kindSpec?.needsChain ?? false;
  const chainSpec = options.chains.find((c) => c.id === chain) ?? null;
  const assetSpec = chainSpec?.assets.find((a) => a.id === asset) ?? null;
  const regionCurrencies = options.regions.find((r) => r.id === region)?.currencies ?? [];

  // A region can only settle the currencies the server says it can — the same
  // table `regionSupportsCurrency` refuses a mismatch with. Keeping the picked
  // currency inside that set means the form cannot compose a 400.
  useEffect(() => {
    if (regionCurrencies.length > 0 && !regionCurrencies.includes(currency)) {
      setCurrency(regionCurrencies[0] as Currency);
    }
  }, [region, regionCurrencies, currency]);

  // A chain carries only its own assets (USDT-TRC20 does not ride on EVM, and
  // money sent to the wrong one is gone). Reset rather than carry a stale pick.
  useEffect(() => {
    if (asset !== '' && !(chainSpec?.assets ?? []).some((a) => a.id === asset)) setAsset('');
  }, [chain, chainSpec, asset]);

  // The acknowledgement is per asset: switching away from a warned asset and
  // back must ask again rather than remember a tick from a different choice.
  useEffect(() => setAcknowledged(false), [asset]);

  const labelValid = label.trim().length >= 3;
  const entityValid = ownerEntity.trim().length >= 3;
  const addressValid =
    address.trim().length >= 6 && (kind !== 'upi' || /^[^@\s]+@[^@\s]+$/.test(address.trim()));
  const limitValid = isAmount(dailyLimit.trim()) && cmp(dailyLimit.trim(), '0') > 0;
  const weightValid = /^\d{1,5}$/.test(weight.trim()) && Number(weight.trim()) <= 10_000;
  const feePctValid =
    /^\d{1,2}(\.\d{1,4})?$/.test(feePct.trim()) &&
    cmp(feePct.trim(), options.feeBounds.maxFeePct) <= 0;
  const feeFixedValid =
    /^\d{1,5}(\.\d{1,4})?$/.test(feeFixed.trim()) &&
    cmp(feeFixed.trim(), options.feeBounds.maxFeeFixed) <= 0;
  const chainValid = !needsChain || (asset !== '' && (chainSpec?.assets ?? []).some((a) => a.id === asset));
  const currencyValid = regionCurrencies.includes(currency);
  const warningAccepted = assetSpec?.warning == null || acknowledged;

  const valid =
    labelValid &&
    entityValid &&
    addressValid &&
    limitValid &&
    weightValid &&
    feePctValid &&
    feeFixedValid &&
    chainValid &&
    currencyValid &&
    warningAccepted;

  async function onCreate(): Promise<void> {
    if (!valid) return;

    const result = await confirm({
      title: 'Register this collection method',
      body: 'It enters the router’s rotation immediately, enabled and healthy — the next matching deposit can be sent to this address. The address cannot be edited afterwards: to retire it, disable the method and register a replacement.',
      confirmLabel: 'Register method',
      facts: [
        { label: 'Kind', value: kind },
        { label: 'Rail', value: needsChain ? `${asset} on ${chain}` : `${region} · ${currency}` },
        { label: 'Credits', value: `${currency} · ${region}` },
        { label: 'Label', value: label.trim() },
        { label: 'Entity', value: ownerEntity.trim() },
        { label: 'Address', value: address.trim() },
        { label: 'Daily cap', value: <Money value={dailyLimit.trim()} currency /> },
        { label: 'Weight', value: weight.trim() },
        { label: 'Modelled fee', value: `${feePct.trim()}% + ${feeFixed.trim()} per deposit` },
        ...(needsChain
          ? [
              {
                label: 'Auto-credit',
                value:
                  assetSpec?.autoCreditCurrency != null
                    ? `yes, as ${assetSpec.autoCreditCurrency}`
                    : 'NO — an operator confirms each deposit with an fx rate',
              },
            ]
          : []),
      ],
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const created = await createPaymentMethod({
        kind,
        label: label.trim(),
        ownerEntity: ownerEntity.trim(),
        address: address.trim(),
        ...(displayName.trim() === '' ? {} : { displayName: displayName.trim() }),
        dailyLimit: dailyLimit.trim(),
        weight: Number(weight.trim()),
        currency,
        region,
        ...(needsChain && asset !== '' ? { chain, asset } : {}),
        feePct: feePct.trim(),
        feeFixed: feeFixed.trim(),
      });
      toast(`${created.label} registered and in rotation.`);
      onDone();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not register the method.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Register a collection method" icon={<IconVault />}>
      <label className="field">
        <span className="field__label">Kind</span>
        <select
          className="select"
          value={kind}
          onChange={(event) => setKind(event.target.value as MethodKind)}
        >
          {options.kinds.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
        <span className="field__hint">
          {needsChain
            ? 'A chain rail must name its chain and its asset — the server refuses one that does not.'
            : 'Collected and reconciled by an operator; nothing is watched automatically.'}
        </span>
      </label>

      <label className="field">
        <span className="field__label">Region</span>
        <select
          className="select"
          value={region}
          onChange={(event) => setRegion(event.target.value as Region)}
        >
          {options.regions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.id} · settles {r.currencies.join(', ')}
            </option>
          ))}
        </select>
        <span className="field__hint">
          A GLOBAL rail is offered to players in every region; anything else is offered only in its
          own.
        </span>
      </label>

      <label className="field">
        <span className="field__label">Currency collected</span>
        <select
          className="select"
          value={currency}
          onChange={(event) => setCurrency(event.target.value as Currency)}
        >
          {regionCurrencies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <span className="field__hint">
          The daily cap below is denominated in this currency, not in rupees. Only currencies this
          region can actually settle are offered.
        </span>
      </label>

      {needsChain ? (
        <>
          <label className="field">
            <span className="field__label">Chain</span>
            <select
              className="select"
              value={chain}
              onChange={(event) => setChain(event.target.value as ChainId)}
            >
              {options.chains.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} · {c.confirmations} confirmations
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Asset</span>
            <select
              className="select"
              value={asset}
              onChange={(event) => setAsset(event.target.value as CryptoAsset | '')}
            >
              <option value="">Choose an asset…</option>
              {(chainSpec?.assets ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.id}
                  {a.autoCreditCurrency ? ` · auto-credits as ${a.autoCreditCurrency}` : ' · manual'}
                </option>
              ))}
            </select>
            {asset === '' ? (
              <span className="field__error">
                A crypto or wallet method must name its asset. USDT-ERC20 and USDT-TRC20 are the same
                token on two chains and money sent to the wrong one is gone.
              </span>
            ) : assetSpec?.autoCreditCurrency != null ? (
              <span className="field__hint">
                Auto-credits {assetSpec.autoCreditCurrency} one-for-one after{' '}
                {chainSpec?.confirmations} confirmations — no rate is needed, so nothing has to be
                converted.
              </span>
            ) : null}
          </label>

          {assetSpec?.warning != null ? (
            <div className="field">
              <span className="field__label">Acknowledge before registering</span>
              <span className="field__error">{assetSpec.warning}</span>
              <label className="field" style={{ flexDirection: 'row', gap: 8, margin: 0 }}>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                <span className="field__hint" style={{ margin: 0 }}>
                  I understand this rail cannot credit a player without an operator.
                </span>
              </label>
            </div>
          ) : null}

          {assetSpec?.autoCreditCurrency != null && assetSpec.autoCreditCurrency !== currency ? (
            <span className="field__error">
              {asset} credits {assetSpec.autoCreditCurrency} one-for-one. Collecting it as {currency}{' '}
              means every deposit needs an operator and an fx rate.
            </span>
          ) : null}
        </>
      ) : null}

      <label className="field">
        <span className="field__label">Label</span>
        <input
          className="input"
          value={label}
          placeholder="UPI · HDFC 9021"
          onChange={(event) => setLabel(event.target.value)}
        />
        <span className="field__hint">How operators recognise it in queues and reports.</span>
      </label>

      <label className="field">
        <span className="field__label">Owner entity</span>
        <input
          className="input"
          value={ownerEntity}
          placeholder="Legal entity holding the account"
          onChange={(event) => setOwnerEntity(event.target.value)}
        />
        <span className="field__hint">Every method must be attributable to a legal entity.</span>
      </label>

      <label className="field">
        <span className="field__label">
          {kind === 'upi'
            ? 'VPA'
            : kind === 'bank'
              ? 'Account reference'
              : kind === 'wallet'
                ? 'Deposit address'
                : 'Address / xpub'}
        </span>
        <input
          className="input num"
          value={address}
          placeholder={
            kind === 'upi'
              ? 'name@bank'
              : kind === 'bank'
                ? 'IFSC / account'
                : kind === 'wallet'
                  ? 'the exchange’s deposit address'
                  : 'xpub…'
          }
          onChange={(event) => setAddress(event.target.value)}
        />
        {address.trim() !== '' && !addressValid ? (
          <span className="field__error">
            {kind === 'upi' ? 'A UPI method needs a VPA like name@bank.' : 'Too short to be real.'}
          </span>
        ) : (
          <span className="field__hint">
            {kind === 'crypto'
              ? 'The extended PUBLIC key. A per-deposit address is derived from it; the key itself is never shown to a player.'
              : 'Written once. Immutable after creation — retire and replace.'}
          </span>
        )}
      </label>

      <label className="field">
        <span className="field__label">Payee display name (optional)</span>
        <input
          className="input"
          value={displayName}
          placeholder="Shown to the player under the QR"
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Daily cap ({currency})</span>
        <input
          className="input num"
          value={dailyLimit}
          placeholder="100000.0000"
          onChange={(event) => setDailyLimit(event.target.value)}
        />
        {dailyLimit.trim() !== '' && !limitValid ? (
          <span className="field__error">Decimal above zero, up to four places.</span>
        ) : (
          <span className="field__hint">What the router measures headroom against.</span>
        )}
      </label>

      <label className="field">
        <span className="field__label">Weight</span>
        <input
          className="input num"
          value={weight}
          onChange={(event) => setWeight(event.target.value)}
        />
        {!weightValid ? (
          <span className="field__error">A whole number from 0 to 10000.</span>
        ) : (
          <span className="field__hint">Relative share among methods with equal headroom.</span>
        )}
      </label>

      <FeeFields
        feePct={feePct}
        feeFixed={feeFixed}
        currency={currency}
        bounds={options.feeBounds}
        pctValid={feePctValid}
        fixedValid={feeFixedValid}
        onPct={setFeePct}
        onFixed={setFeeFixed}
      />

      <div className="row-actions row-actions--start">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || !valid}
          onClick={onCreate}
        >
          <IconCheck size="sm" />
          {busy ? 'Registering…' : 'Register method'}
        </button>
      </div>
    </Card>
  );
}

/**
 * The two MODELLED fee figures (ADR-053).
 *
 * The wording matters more than the inputs do. These never touch a player
 * amount — a deposit credits in full whatever is typed here — and the only
 * thing they change is the "modelled rail fees" line in the NGR report, which
 * read ₹0 forever because no endpoint and no screen could set them. A wrong
 * profitability number is worse than a missing one, so the hint says exactly
 * what the figures are and what they are not.
 */
function FeeFields({
  feePct,
  feeFixed,
  currency,
  bounds,
  pctValid,
  fixedValid,
  onPct,
  onFixed,
}: {
  feePct: string;
  feeFixed: string;
  currency: Currency;
  bounds: { maxFeePct: string; maxFeeFixed: string };
  pctValid: boolean;
  fixedValid: boolean;
  onPct: (value: string) => void;
  onFixed: (value: string) => void;
}): ReactNode {
  return (
    <>
      <label className="field">
        <span className="field__label">Rail fee — percentage</span>
        <input
          className="input num"
          value={feePct}
          placeholder="2.5"
          onChange={(event) => onPct(event.target.value)}
        />
        {!pctValid ? (
          <span className="field__error">
            A decimal up to {bounds.maxFeePct.replace(/\.0+$/, '')}% — “2.5” means 2.5%.
          </span>
        ) : (
          <span className="field__hint">
            What this rail charges US to collect. Never charged to a player: the deposit credits in
            full. It is subtracted in the NGR report as a modelled cost.
          </span>
        )}
      </label>

      <label className="field">
        <span className="field__label">Rail fee — fixed per deposit ({currency})</span>
        <input
          className="input num"
          value={feeFixed}
          placeholder="0"
          onChange={(event) => onFixed(event.target.value)}
        />
        {!fixedValid ? (
          <span className="field__error">
            A decimal up to {bounds.maxFeeFixed.replace(/\.0+$/, '')}, in this rail’s currency.
          </span>
        ) : (
          <span className="field__hint">
            Added to the percentage per credited deposit. Leave at 0 when the rail charges only a
            percentage.
          </span>
        )}
      </label>
    </>
  );
}

/**
 * The per-transaction deposit and withdrawal bounds and the payout fee (T-192).
 *
 * These four figures were module constants in the two payment services, whose
 * own comments said they should move into the database "once the admin panel
 * can edit it" — so until now raising a deposit floor was a code deploy. They
 * are one audited `platform_settings` key, authored in INR and restated per
 * currency by the server exactly as they always were.
 *
 * The whole set is submitted on every save, deliberately: the server validates
 * a settings value in isolation and the rules here are RELATIONS (min below
 * max; a payout fee that can never exceed the minimum withdrawal), so a partial
 * write could pass validation and leave a rail whose every amount is out of
 * range and which therefore silently accepts nothing.
 */
function LimitsCard(): ReactNode {
  const limits = useAsync(() => fetchPaymentLimits(), []);
  const [draft, setDraft] = useState<PaymentLimits | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  useEffect(() => {
    if (limits.data) setDraft(limits.data);
  }, [limits.data]);

  const FIELDS: { key: keyof PaymentLimits; label: string; hint: string }[] = useMemo(
    () => [
      {
        key: 'minDepositInr',
        label: 'Minimum deposit',
        hint: 'Below this a deposit is refused before any rail headroom is reserved.',
      },
      {
        key: 'maxDepositInr',
        label: 'Maximum deposit',
        hint: 'A single deposit cannot exceed this. A rail’s own daily cap still applies on top.',
      },
      {
        key: 'minWithdrawalInr',
        label: 'Minimum withdrawal',
        hint: 'The floor a payout request is measured against.',
      },
      {
        key: 'maxWithdrawalInr',
        label: 'Maximum withdrawal',
        hint: 'A player above this splits the payout into several requests.',
      },
      {
        key: 'withdrawalFeeInr',
        label: 'Payout fee',
        hint: 'Deducted from what the player receives, never added on top — the debit is always exactly what they asked for. It can never exceed the minimum withdrawal.',
      },
    ],
    [],
  );

  const current = limits.data;
  const values = draft;
  const shapeValid =
    values !== null && FIELDS.every((f) => /^\d+(\.\d{1,4})?$/.test(values[f.key].trim()));
  const relationsValid =
    values !== null &&
    shapeValid &&
    cmp(values.minDepositInr, '0') > 0 &&
    cmp(values.minWithdrawalInr, '0') > 0 &&
    cmp(values.maxDepositInr, values.minDepositInr) > 0 &&
    cmp(values.maxWithdrawalInr, values.minWithdrawalInr) > 0 &&
    cmp(values.withdrawalFeeInr, values.minWithdrawalInr) <= 0;
  const changed =
    values !== null &&
    current !== undefined &&
    current !== null &&
    FIELDS.some((f) => cmpSafe(values[f.key], current[f.key]) !== 0);

  async function onSave(): Promise<void> {
    if (!values || !relationsValid || !changed) return;

    const result = await confirm({
      title: 'Change the payment limits',
      body: 'These bind on the next deposit and the next payout request — there is no deploy. Every figure is authored in INR and restated in the player’s own currency at the denomination scale (ADR-020), so a USDT rail keeps a USDT-shaped floor.',
      confirmLabel: 'Save limits',
      facts: FIELDS.filter((f) => cmpSafe(values[f.key], current?.[f.key] ?? '0') !== 0).map((f) => ({
        label: f.label,
        value: `₹${current?.[f.key] ?? '—'} → ₹${values[f.key]}`,
      })),
      note: { label: 'Reason', required: true },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updatePaymentLimits({
        minDepositInr: values.minDepositInr.trim(),
        maxDepositInr: values.maxDepositInr.trim(),
        minWithdrawalInr: values.minWithdrawalInr.trim(),
        maxWithdrawalInr: values.maxWithdrawalInr.trim(),
        withdrawalFeeInr: values.withdrawalFeeInr.trim(),
      });
      toast('Payment limits saved — live on the next request.');
      limits.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save the limits.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Deposit & withdrawal limits" icon={<IconSliders />}>
      <span className="toolbar__note">
        Authored in INR and restated in every other currency at the denomination scale — one number
        to reason about, and no FX rate inside the policy. A player’s OWN deposit limits (ADR-070)
        are separate and are checked first; these are the platform’s per-transaction bounds.
      </span>

      {limits.error && !values ? (
        <ErrorState title="The limits did not load" detail={limits.error} onRetry={limits.reload} />
      ) : !values ? (
        <Loading>Reading the limits…</Loading>
      ) : (
        <>
          {FIELDS.map((f) => (
            <label className="field" key={f.key}>
              <span className="field__label">{f.label} (₹)</span>
              <input
                className="input num"
                value={values[f.key]}
                onChange={(event) =>
                  setDraft({ ...values, [f.key]: event.target.value } as PaymentLimits)
                }
              />
              <span className="field__hint">{f.hint}</span>
            </label>
          ))}

          {!shapeValid ? (
            <span className="field__error">
              Every figure is a non-negative decimal with up to four places.
            </span>
          ) : !relationsValid ? (
            <span className="field__error">
              Each minimum must be above zero and below its maximum, and the payout fee can never
              exceed the minimum withdrawal.
            </span>
          ) : null}

          <div className="row-actions row-actions--start">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busy || !relationsValid || !changed}
              onClick={onSave}
            >
              <IconCheck size="sm" />
              {busy ? 'Saving…' : 'Save limits'}
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              disabled={busy || !changed}
              onClick={() => setDraft(limits.data ?? null)}
            >
              Discard
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

/** `cmp` refuses a malformed amount; a half-typed field must not throw mid-render. */
function cmpSafe(a: string, b: string): number {
  try {
    return cmp(a.trim(), b.trim());
  } catch {
    return a.trim() === b.trim() ? 0 : 1;
  }
}

function MethodRow({
  row,
  onChanged,
}: {
  row: AdminPaymentMethod;
  onChanged: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const headroom = sub(row.dailyLimit, row.usedToday);
  const usage = percent(row.usedToday, row.dailyLimit);
  const exhausted = cmp(headroom, '0') <= 0;
  const selectable = row.enabled && row.health === 'healthy' && !exhausted;

  async function toggle(): Promise<void> {
    const next = !row.enabled;
    const result = await confirm({
      title: next ? 'Enable this collection method' : 'Disable this collection method',
      intent: next ? 'default' : 'danger',
      body: next
        ? 'The router may start sending deposits to this account again.'
        : 'The router stops selecting this account. Deposits already issued against it stay valid and can still be paid — disabling does not invalidate an outstanding QR.',
      confirmLabel: next ? 'Enable' : 'Disable',
      facts: [
        { label: 'Method', value: row.label },
        { label: 'Entity', value: row.ownerEntity },
        { label: 'Headroom today', value: <Money value={headroom} currency /> },
      ],
      note: { label: 'Reason', required: !next },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updatePaymentMethod(row.id, { enabled: next });
      toast(`${row.label} ${next ? 'enabled' : 'disabled'}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the method.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function setHealth(health: MethodHealth): Promise<void> {
    const result = await confirm({
      title: `Mark ${row.label} as ${health}`,
      intent: health === 'healthy' ? 'default' : 'danger',
      body:
        health === 'healthy'
          ? 'The router will select this method again.'
          : 'The router never selects a method that is not healthy. Deposits move to the remaining accounts.',
      confirmLabel: `Mark ${health}`,
      facts: [
        { label: 'Method', value: row.label },
        { label: 'Current health', value: row.health },
      ],
      note: { label: 'Reason', required: health !== 'healthy' },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updatePaymentMethod(row.id, { health });
      toast(`${row.label} marked ${health}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the method.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function editLimit(): Promise<void> {
    const next = window.prompt(`Daily cap for ${row.label} (decimal, 4 places)`, row.dailyLimit);
    if (next === null) return;
    if (!isAmount(next.trim())) {
      toast('The cap must be a decimal with up to four places.', 'error');
      return;
    }
    const value = next.trim();
    const result = await confirm({
      title: 'Change the daily cap',
      body: 'The cap is what the router measures headroom against. Lowering it below what has already been collected today takes the method out of rotation until the counter resets.',
      confirmLabel: 'Change cap',
      facts: [
        { label: 'Method', value: row.label },
        { label: 'Current cap', value: <Money value={row.dailyLimit} currency /> },
        { label: 'New cap', value: <Money value={value} currency /> },
        { label: 'Used today', value: <Money value={row.usedToday} currency /> },
      ],
      note: { label: 'Reason', required: true },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updatePaymentMethod(row.id, { dailyLimit: value });
      toast(`Cap updated for ${row.label}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the cap.', 'error');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The modelled rail cost (ADR-053). Two prompts rather than one, because they
   * are two different units and a single combined box invites "2.5 + 3" typed
   * into a percentage field. Nothing here reaches a player: the deposit credits
   * in full and these figures move only the NGR report's fee line — which read
   * ₹0 forever, because until T-192 no endpoint accepted them at all.
   */
  async function editFees(): Promise<void> {
    const pct = window.prompt(
      `Rail fee percentage for ${row.label} — "2.5" means 2.5%`,
      row.feePct,
    );
    if (pct === null) return;
    const fixed = window.prompt(
      `Fixed fee per deposit for ${row.label} (${row.currency})`,
      row.feeFixed,
    );
    if (fixed === null) return;
    if (!isAmount(pct.trim()) || !isAmount(fixed.trim())) {
      toast('Both figures are decimals with up to four places.', 'error');
      return;
    }

    const result = await confirm({
      title: 'Change the modelled rail fee',
      body: 'This is what the rail costs US, not a charge to the player — a deposit still credits in full. It is subtracted in the NGR report as a modelled cost, so an invented number here produces a wrong profitability figure.',
      confirmLabel: 'Change fee',
      facts: [
        { label: 'Method', value: row.label },
        { label: 'Percentage', value: `${row.feePct}% → ${pct.trim()}%` },
        { label: 'Fixed per deposit', value: `${row.feeFixed} → ${fixed.trim()} ${row.currency}` },
      ],
      note: { label: 'Reason', required: true },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updatePaymentMethod(row.id, { feePct: pct.trim(), feeFixed: fixed.trim() });
      toast(`Modelled fee updated for ${row.label}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the fee.', 'error');
    } finally {
      setBusy(false);
    }
  }

  const usageLevel = usageTone(usage);
  const railLabel =
    row.asset !== null && row.chain !== null ? `${row.asset} · ${row.chain}` : row.region;

  return (
    <tr>
      <td className="nowrap cell-lead">
        {row.label}
        <span className="cell-sub">{row.addressMasked}</span>
      </td>
      <td className="muted">{row.kind}</td>
      <td className="muted nowrap">
        {row.currency}
        <span className="cell-sub">{railLabel}</span>
      </td>
      <td className="muted nowrap">{row.ownerEntity}</td>
      <td>
        <StatusBadge status={row.health} />
      </td>
      <td className="right mono dim">{row.weight}</td>
      <td className="right mono">
        <Money value={row.dailyLimit} />
      </td>
      <td className="right mono">
        <Money value={row.usedToday} />
      </td>
      <td className="right mono">
        <span className={exhausted ? 'stat__value--loss' : ''}>
          <Money value={headroom} />
        </span>
      </td>
      <td className="right">
        <UsageMeter percentage={usage} level={usageLevel} />
      </td>
      <td
        className="right mono dim nowrap"
        title="Modelled cost of this rail — never charged to a player; feeds the NGR report"
      >
        {trimZeros(row.feePct)}%
        {cmp(row.feeFixed, '0') > 0 ? ` + ${trimZeros(row.feeFixed)}` : ''}
      </td>
      <td className="right mono dim">{row.depositsToday}</td>
      <td>
        <div className="row-actions">
          {!selectable ? (
            <span className="badge badge--neutral" title="Not currently selectable by the router">
              idle
            </span>
          ) : null}
          <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={editLimit}>
            <IconSliders size="sm" />
            Cap
          </button>
          <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={editFees}>
            Fee
          </button>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            disabled={busy}
            onClick={() => setHealth(row.health === 'healthy' ? 'down' : 'healthy')}
          >
            {row.health === 'healthy' ? 'Mark down' : 'Mark healthy'}
          </button>
          <button
            type="button"
            className={`btn btn--sm ${row.enabled ? 'btn--danger' : 'btn--primary'}`}
            disabled={busy}
            onClick={toggle}
          >
            {row.enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Display only: "2.5000" reads as "2.5". A percentage is a decimal STRING all
 * the way through the panel — this trims trailing zeros textually and never
 * turns the figure into a number to do it.
 */
function trimZeros(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
}

function usageTone(usage: string | null): 'ok' | 'watch' | 'alert' {
  if (usage === null) return 'ok';
  if (cmp(usage, '90') >= 0) return 'alert';
  if (cmp(usage, '70') >= 0) return 'watch';
  return 'ok';
}
